<script setup lang="ts">
import { watch } from 'vue'
import { useDocumentViewer } from '@/composables/useDocumentViewer'
import { useDocumentsStore } from '@/views/Documents/store'
import { Button } from '@/components/ui/button'
import { FileText, Maximize2, X, Loader2 } from 'lucide-vue-next'

const {
  isMinimized,
  currentDocumentId,
  minimizedDocuments,
  restoreFromMinimized,
  removeFromMinimized,
  addToMinimized,
} = useDocumentViewer()

const documentsStore = useDocumentsStore()

// When restored from storage as minimized, fetch the doc and add to minimized list
watch(
  [isMinimized, currentDocumentId, () => minimizedDocuments.value.length],
  async ([minimized, docId, count]) => {
    if (minimized && docId && count === 0) {
      const doc = await documentsStore.fetchDocumentById(docId)
      if (doc) {
        addToMinimized(doc)
      }
    }
  },
  { immediate: true },
)

const handleRestore = (docId: string) => {
  restoreFromMinimized(docId)
}
</script>

<template>
  <!-- Minimized documents stack -->
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    leave-active-class="transition-all duration-200 ease-in"
    enter-from-class="opacity-0 translate-y-4 scale-95"
    enter-to-class="opacity-100 translate-y-0 scale-100"
    leave-from-class="opacity-100 translate-y-0 scale-100"
    leave-to-class="opacity-0 translate-y-4 scale-95"
  >
    <div
      v-if="isMinimized && minimizedDocuments.length > 0"
      class="fixed bottom-36 right-4 z-[90] space-y-2"
    >
      <div
        v-for="doc in minimizedDocuments"
        :key="doc.id"
        class="bg-background border rounded-lg shadow-lg p-3 flex items-center gap-3 w-64"
      >
        <div class="flex-shrink-0 h-8 w-8 rounded bg-primary/10 flex items-center justify-center">
          <FileText class="h-4 w-4 text-primary" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium truncate">{{ doc.title }}</p>
          <p class="text-xs text-muted-foreground">Minimizado</p>
        </div>
        <div class="flex items-center gap-1 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7"
            @click="handleRestore(doc.id)"
            title="Restaurar"
          >
            <Maximize2 class="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7 text-muted-foreground hover:text-destructive"
            @click="removeFromMinimized(doc.id)"
            title="Cerrar"
          >
            <X class="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  </Transition>
</template>
